import { ChangeEvent, memo, useCallback, useEffect, useReducer, useState } from 'react';
import { Button, Tab, Tabs } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Equifax_ConsumerCreditReportResult,
  Estate,
  EstateResult,
  EstateStatus,
  EstateType,
  PortalPermissions,
  usePortalMembership_GetPortalMembershipsByPortalIdLazyQuery,
} from 'queries';
import {
  EmailVerificationType,
  useAuth_EmailVerificationMutation,
  useAuth_ImpersonateMutation,
  useEquifax_CheckTwnTranscriptStatusReportLazyQuery,
  useEquifax_RetrieveTwnTranscriptReportLazyQuery,
  useEstate_GetAllEstatesQuery,
  useEstate_UpdateEstateMutation,
  useEstate_VerifyDocumentsMutation,
} from 'queries';

import Spinner from 'common/spinner';
import { EstateDashboardTab } from 'pages/estate-dashboard/estate-dashboard';
import { activeStatuses, EstateStatusLabel } from 'utils/estate';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import EstateActionsGrid from './estate-actions-grid';
import PullReportDataModal from './pull-report-data-modal';
import PurgeModal from './purge-modal';

const useStyles = makeStyles(() => ({
  tabs: { minHeight: '15px' },
  tab: { minHeight: '15px', minWidth: '60px' },
}));

enum EstatesActionType {
  SET_ESTATES,
  REPLACE_ESTATE,
  REMOVE_ESTATE,
}

type EstatesAction = {
  type: EstatesActionType;
  estates?: Estate[];
  oldEstate?: Estate;
  newEstate?: Estate;
  removeEstate?: Estate;
  tabSelected: number;
};

const estatesFilter = (estates: Estate[], tabSelected: number) => {
  return estates.filter((estate) => {
    switch (tabSelected) {
      case 0: {
        return activeStatuses().includes(estate.status);
      }
      case 1: {
        return estate.status === EstateStatus.Completed;
      }
      default: {
        return activeStatuses().includes(estate.status);
      }
    }
  });
};

const estatesReducer = (
  estates: Estate[],
  action: EstatesAction,
  tabSelected: number,
): Estate[] => {
  switch (action.type) {
    case EstatesActionType.SET_ESTATES: {
      const updatedEstates = [...estatesFilter(action.estates!, tabSelected)];
      return updatedEstates;
    }
    case EstatesActionType.REPLACE_ESTATE: {
      const updatedEstates = [
        ...estatesFilter(
          estates.map((estate) => (estate === action.oldEstate! ? action.newEstate! : estate)),
          tabSelected,
        ),
      ];
      return updatedEstates;
    }
    case EstatesActionType.REMOVE_ESTATE: {
      const updatedEstates = [
        ...estatesFilter(
          estates.filter((estate) => estate.id !== action.removeEstate!.id),
          tabSelected,
        ),
      ];
      return updatedEstates;
    }
    default:
      return estates;
  }
};

const initialEstates: Estate[] = [];

const EstateActions = () => {
  const classes = useStyles();

  const [tabSelected, setTabSelected] = useState<number>(0);

  const [estates, estatesDispatch] = useReducer(
    (estates: Estate[], action: EstatesAction) => estatesReducer(estates, action, tabSelected),
    initialEstates,
  );

  const [currentEstate, setCurrentEstate] = useState<Estate | null>(null);
  const [showPullReportDataModal, setShowPullReportDataModal] = useState<boolean>(false);
  const [showPurgeModal, setShowPurgeModal] = useState<boolean>(false);
  const [purgeAllExpiredEstateResults, setPurgeAllExpiredEstateResults] = useState<boolean>(false);
  const [reportEstateId, setReportEstateId] = useState<string>();
  const [reportPortalId, setReportPortalId] = useState<string>();

  const setEstates = useCallback((estates: Estate[], tabSelected: number) => {
    estatesDispatch({
      type: EstatesActionType.SET_ESTATES,
      estates,
      tabSelected,
    });
  }, []);

  const setReplaceEstate = (oldEstate: Estate, newEstate: Estate, tabSelected: number) => {
    estatesDispatch({
      type: EstatesActionType.REPLACE_ESTATE,
      oldEstate,
      newEstate,
      tabSelected,
    });
  };

  const setRemoveEstate = (removeEstate: Estate, tabSelected: number) => {
    estatesDispatch({
      type: EstatesActionType.REMOVE_ESTATE,
      removeEstate,
      tabSelected,
    });
  };

  const getAllEstatesFilter = useCallback((tabSelected: number) => {
    return tabSelected
      ? {
          filter: {
            statuses: [EstateStatus.Completed],
          },
        }
      : {
          filter: {
            statuses: activeStatuses(),
          },
        };
  }, []);

  const {
    data: estatesData,
    loading: estatesLoading,
    refetch: refetchEstates,
  } = useEstate_GetAllEstatesQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: getAllEstatesFilter(tabSelected),
    onError: (e) => {
      showAlert({
        message: 'Error getting all estates: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const resetEstates = useCallback(
    async (newTabSelected: number) => {
      const { data: updatedEstatesData } = await refetchEstates(
        getAllEstatesFilter(newTabSelected),
      );

      // @ts-expect-error - Missing required properties
      setEstates(updatedEstatesData.data, newTabSelected);
    },
    [getAllEstatesFilter, refetchEstates, setEstates],
  );

  const [loadPortalMemberships] = usePortalMembership_GetPortalMembershipsByPortalIdLazyQuery({
    onError: (e) => {
      showAlert({
        message: 'Error getting all portal memberships: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [estateVerifyDocumentsMutation] = useEstate_VerifyDocumentsMutation({
    onError: (e) => {
      showAlert({
        message: 'Error verifying documents: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });
  const handleDocumentsVerified = async (id: string) => {
    await estateVerifyDocumentsMutation({
      variables: {
        id: id,
      },
      onCompleted: ({ data: updatedEstate }) => {
        if (updatedEstate) {
          setReplaceEstate(currentEstate!, { ...(updatedEstate as Estate) }, tabSelected);

          showToast({
            message: 'Documents marked as verified.',
            severity: NotificationSeverity.SUCCESS,
          });
        }
      },
      onError: (e) => {
        showAlert({
          message: 'Error verifying documents: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });
  };

  const [emailVerificationMutation] = useAuth_EmailVerificationMutation({
    onError: (e) =>
      showAlert({
        message: 'Error sending email: ' + e.message,
        severity: NotificationSeverity.ERROR,
      }),
  });
  const handleSendConsent = async (id: string, portalId: string, estateType: string) => {
    const getVerificationType = (estateType: string) => {
      switch (estateType) {
        case EstateType.Truadmin:
          return EmailVerificationType.EstateClientConsentTruadmin;
        case EstateType.Truplanning:
          return EmailVerificationType.EstateClientConsentTruplanning;
        case EstateType.Truproxy:
          return EmailVerificationType.EstateClientConsentTruproxy;
        case EstateType.Trustadmin:
          return EmailVerificationType.EstateClientConsentTrustadmin;
        default:
          return null;
      }
    };

    const verificationType = getVerificationType(estateType);

    if (!verificationType) {
      showAlert({
        message: 'Error sending email: Invalid estate type.',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    await emailVerificationMutation({
      variables: {
        input: {
          estateId: id,
          verificationType: verificationType,
        },
      },
      onCompleted: () => {
        showToast({ message: 'Email has been sent.', severity: NotificationSeverity.SUCCESS });
      },
      onError: (e) => {
        showAlert({
          message: 'Error sending email: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });
    resetEstates(tabSelected);
  };

  const [impersonate] = useAuth_ImpersonateMutation({
    onError: (e) => {
      showAlert({
        message: 'Error impersonating user: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });
  const handleImpersonateView = async ({
    type,
    portalId,
    estateId,
  }: {
    type: string;
    portalId: string;
    estateId?: string;
    documentsFolderId?: string;
  }) => {
    const { data: portalMembershipData } = await loadPortalMemberships({
      variables: {
        input: {
          portalId: portalId,
          permission: PortalPermissions.Admin,
        },
      },
    });

    // find the portal owner
    const portalOwner = portalMembershipData?.data.find(
      (portalMembership) => portalMembership.isPortalOwner,
    );

    if (!portalOwner || !portalOwner.userId) {
      showAlert({
        message: 'Error loading documents, no portal owner.',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    const typeUrl: { [key: string]: string } = {
      documents: `/home/${portalId}/estate/${estateId}/estate-dashboard?tab=${EstateDashboardTab.VAULT}`, // send to the documents folder
      estate: `/home/${portalId}/estate/${estateId}/estate-dashboard?tab=${EstateDashboardTab.ESTATE_CHECKLIST}`, // send to the estate dashboard
      portal: `/home/${portalId}/home`, // send to the portal page
    };

    await impersonate({
      variables: { userId: portalOwner.userId, returnUrl: '/admin/estates' },
      onCompleted: () => {
        // react history.push() doesn't work because a page reload is needed for impersonate to take effect
        // TODO: Remove the need of a page refresh for impersonate to take effect.
        window.location.href = typeUrl[type];
      },
    });
  };

  const [updateEstate] = useEstate_UpdateEstateMutation({
    onError: (e) => {
      showAlert({
        message: 'Error updating estate: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });
  const handleClearCache = (estateId: string, portalId: string) => {
    updateEstate({
      variables: {
        input: {
          portalId: portalId,
          id: estateId,
          ccrDate: null,
          pvDate: null,
        },
      },
      onCompleted: () => {
        showToast({
          message: 'The results cache has been cleared.',
          severity: NotificationSeverity.SUCCESS,
        });
      },
    });
  };

  const handlePurgeEstate = (estateId: string, portalId: string) => {
    setReportPortalId(portalId);
    setReportEstateId(estateId);
    setCurrentEstate(estates.find((estate) => estate.id === estateId)!);
    setShowPurgeModal(true);
  };

  const handlePurgeAllExpiredResultsClick = () => {
    setPurgeAllExpiredEstateResults(true);
    setShowPurgeModal(true);
  };

  const handleMarkConsentReceived = (estateId: string, portalId: string, estateType: string) => {
    updateEstate({
      variables: {
        input: {
          portalId: portalId,
          id: estateId,
          clientConsentReceived: true,
          status:
            estateType === EstateType.Truadmin || EstateType.Trustadmin
              ? EstateStatus.ResultsReady
              : // else EstateType.Truplanning and EstateType.Truproxy
                EstateStatus.TrustateApproval,
        },
      },
      onCompleted: () => {
        showToast({
          message: 'Consent has been recorded.',
          severity: NotificationSeverity.SUCCESS,
        });
      },
    });
  };

  const handlePullReportData = (estateId: string) => {
    setReportEstateId(estateId);
    setReportPortalId('');
    setCurrentEstate(estates.find((estate) => estate.id === estateId)!);
    setShowPullReportDataModal(true);
  };

  const onPullReportDataComplete = () => {
    resetEstates(tabSelected);
    setShowPullReportDataModal(false);
  };

  const handleChangeStatus = (
    status: EstateStatus,
    estate: Estate,
    portalId: string,
    tabSelected: number,
  ) => {
    setRemoveEstate(estate, tabSelected);

    updateEstate({
      variables: {
        input: {
          portalId: portalId,
          id: estate.id,
          status: status,
        },
      },
      onCompleted: () => {
        const statusLabel = EstateStatusLabel[status].adminLabel || EstateStatusLabel[status].label;
        showToast({
          message: `Status has been changed to: ${statusLabel}.`,
          severity: NotificationSeverity.SUCCESS,
        });
        // refetch, may move to another tab
        resetEstates(tabSelected);
      },
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTabChange = (e: ChangeEvent<any>, newTabSelected: number) => {
    setTabSelected(newTabSelected);
    resetEstates(newTabSelected);
  };

  // Removed because we're not signing 4506C in app at this time
  // const [loadTwnTaxTranscriptRequest] = useEquifax_RequestTwnTranscriptReportLazyQuery({
  //   fetchPolicy: 'network-only',
  //   notifyOnNetworkStatusChange: true,
  //   onError: (e) => {
  //     showToast({
  //       message: 'Error requesting twn transcript report: ' + e.message,
  //       severity: NotificationSeverity.ERROR,
  //     });
  //   },
  // });

  // Removed because we're not signing 4506C in app at this time
  // const handleRequestTaxTranscriptsClick = (estate: Estate) => {
  //   if (!estate.documentIdSigned4506C) {
  //     showToast({
  //       message: 'Missing 4506c signature.',
  //       severity: NotificationSeverity.SUCCESS,
  //     });
  //   } else {
  //     loadTwnTaxTranscriptRequest({
  //       variables: {
  //         input: {
  //           estateId: estate.id,
  //           firstName: estate.estateTargetFirstName,
  //           lastName: estate.estateTargetLastName,
  //           socialNum: estate.estateTargetSsn,
  //         },
  //       },
  //     }).then(() => {
  //       resetEstates(tabSelected);
  //       showToast({
  //         message: 'Tax transcripts requested.',
  //         severity: NotificationSeverity.SUCCESS,
  //       });
  //     });
  //   }
  // };

  const [loadTwnTaxTranscriptStatusCheck] = useEquifax_CheckTwnTranscriptStatusReportLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: (e) => {
      showAlert({
        message: 'Error checking twn transcript status report: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleStatusCheckTaxTranscriptsClick = (estate: Estate) => {
    loadTwnTaxTranscriptStatusCheck({
      variables: {
        input: {
          estateId: estate.id,
          serverId: estate.results![0].twnServerId || '',
          transactionId: estate.results![0].twnTransactionId,
        },
      },
    }).then((result) => {
      resetEstates(tabSelected);
      showToast({
        message: `Transcript Status: ${result.data?.data.estateResult.twnStatus}`,
        severity: NotificationSeverity.SUCCESS,
      });
    });
  };

  const [loadTwnTaxTranscriptReport] = useEquifax_RetrieveTwnTranscriptReportLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: (e) => {
      showAlert({
        message: 'Error retrieving twn transcript report: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleRetrieveTaxTranscriptsClick = async (estate: Estate) => {
    loadTwnTaxTranscriptReport({
      variables: {
        input: {
          estateId: estate.id,
          serverId: estate.results![0].twnServerId,
          transactionId: estate.results![0].twnTransactionId,
        },
      },
    }).then(() => {
      resetEstates(tabSelected);
      showToast({
        message: 'Tax transcripts retrieved.',
        severity: NotificationSeverity.SUCCESS,
      });
    });
  };

  useEffect(() => {
    if (estatesData && !estates.length) {
      // @ts-expect-error - Missing required properties
      setEstates(estatesData.data, tabSelected);
    }
  }, [estates.length, estatesData, resetEstates, setEstates, tabSelected]);

  if (estatesLoading) {
    return <Spinner />;
  }

  return (
    <>
      <PurgeModal
        estateId={reportEstateId!}
        portalId={reportPortalId!}
        purgeAllExpiredEstateResults={purgeAllExpiredEstateResults}
        setPurgeAllExpiredEstateResults={setPurgeAllExpiredEstateResults}
        setShowModal={setShowPurgeModal}
        showModal={showPurgeModal}
        onPurgeReportDataComplete={() => {
          if (purgeAllExpiredEstateResults) {
            // Refetch / refresh all estates
            resetEstates(tabSelected);
          } else {
            // Refetch / refresh the purged estate
            setReplaceEstate(
              currentEstate!,
              { ...currentEstate!, pvDate: null, ccrDate: null, results: [] },
              tabSelected,
            );
          }
        }}
      />
      <PullReportDataModal
        estateId={reportEstateId!}
        setShowModal={setShowPullReportDataModal}
        showModal={showPullReportDataModal}
        onPullReportDataComplete={(
          //pvResultData: Equifax_PropertyVerificationResult,
          ccrResultData: Equifax_ConsumerCreditReportResult,
        ) => {
          if (currentEstate) {
            // Removed TRU-820
            // const pvResults =
            //   pvResultData && pvResultData.estateResultItems
            //     ? [
            //         ...pvResultData.estateResultItems.map((estateResultItem) => {
            //           return { estateId: reportEstateId!, ...estateResultItem } as EstateResult;
            //         }),
            //       ]
            //     : [];

            const ccrResults =
              ccrResultData && ccrResultData.estateResultItems
                ? [
                    ...ccrResultData.estateResultItems.map((estateResultItem) => {
                      return { estateId: reportEstateId!, ...estateResultItem } as EstateResult;
                    }),
                  ]
                : [];

            setReplaceEstate(
              currentEstate,
              {
                ...currentEstate,
                //pvDate: pvResultData.pvReportDate,
                ccrDate: ccrResultData.ccrReportDate,
                results: [...ccrResults],
              },
              tabSelected,
            );
          }
          onPullReportDataComplete();
        }}
      />
      <div style={{ flex: 3 }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handlePurgeAllExpiredResultsClick}
        >
          Purge All Expired Results
        </Button>
      </div>
      <Tabs
        value={tabSelected}
        indicatorColor="primary"
        textColor="primary"
        onChange={handleTabChange}
        className={classes.tabs}
      >
        <Tab className={classes.tab} label="In Process" />
        <Tab className={classes.tab} label="Completed" />
      </Tabs>
      <EstateActionsGrid
        estates={estates}
        handleImpersonateView={handleImpersonateView}
        handleDocumentsVerified={(estateId: string) => {
          setCurrentEstate(estates.find((estate) => estate.id === estateId)!);
          handleDocumentsVerified(estateId);
        }}
        handleSendConsent={handleSendConsent}
        handleMarkConsentReceived={handleMarkConsentReceived}
        handleClearCache={handleClearCache}
        handlePurgeEstate={handlePurgeEstate}
        handlePullReportData={handlePullReportData}
        handleChangeStatus={(status: EstateStatus, estate: Estate, portalId: string) =>
          handleChangeStatus(status, estate, portalId, tabSelected)
        }
        // handleRequestTaxTranscriptsClick={handleRequestTaxTranscriptsClick}
        handleStatusCheckTaxTranscriptsClick={handleStatusCheckTaxTranscriptsClick}
        handleRetrieveTaxTranscriptsClick={handleRetrieveTaxTranscriptsClick}
      />
    </>
  );
};

export default memo(EstateActions);
